import { EMPTY, fromEvent, merge, Observable } from 'rxjs';
import { distinctUntilChanged, map, mapTo, share, tap } from 'rxjs/operators';
import { findAncestor, isNode } from '../../../helpers/dom/node';
import {
  getSelectedCellsPlan,
  isSameTablePlan,
  isTableCellElement,
  isTableElement,
} from '../../../helpers/dom/table';
import { fromMouseTracking } from '../../../helpers/fromMouseTracking';
import {
  createConnectingService,
  ElementSelectionService,
  NULL_SERVICE,
  Service,
  UndoService,
} from '../../../index';
import { ElementService } from '../../dom/elementService';
import { ColumnResizeController } from './columnResizeController';
import {
  decorateTableForEdition,
  isButtonPanel,
  isColumnHeaderDecorator,
  isColumnResizeHandle,
  isHeaderDecorator,
  isHeaderDecoratorDecorated,
  isRowHeaderDecorator,
  isTableCornerButtonDecorator,
  isTableFullySelected,
  isTableWrapper,
  undecorateAllTableForEdition,
} from './decorateTable';
import { arrowKeyCode, TableEditionController } from './tableEditionController';

export function createHtmlTableEffectService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  undoService: UndoService,
): Service {
  return createConnectingService(
    element$,
    () => NULL_SERVICE,
    element => new HtmlTableEffectService(element, selectionService, undoService),
  );
}

export class HtmlTableEffectService extends ElementService<Element> {
  constructor(element: Element, selectionService: ElementSelectionService, undoService: UndoService) {
    super(element, selectionService, {
      effects$: createHtmlTableEffect(element, selectionService, undoService),
    });
  }
}

function createHtmlTableEffect(
  element: Element,
  selectionService: ElementSelectionService,
  undoService: UndoService,
): Observable<unknown> {
  const document = element.ownerDocument;
  if (!document) {
    return EMPTY;
  }

  const window = document.defaultView;
  if (!window) {
    return EMPTY;
  }

  const tableUnderClick$ = getTableUnderMousedownStream(element);
  const cornerButtonUnderClick$ = getTableCornerButtonUnderClickStream(element);
  const handleUnderClick$ = getHandleUnderClickStream(element);
  const cellsSelectedPlan$ = getSelectedCellsPlanStream(element, selectionService);
  const tableEditionController = new TableEditionController(element, selectionService);

  return merge(
    // Decorate table when it's clicked.
    tableUnderClick$.pipe(
      tap(table => {
        if (table && getCanDecorateTable(table, element)) {
          decorateTableForEdition(table);
        } else {
          undecorateAllTableForEdition(element);
        }
      }),
    ),
    // Select the whole table when its corner button is clicked.
    cornerButtonUnderClick$.pipe(
      tap(cornerButton => {
        if (!cornerButton) {
          return;
        }
        const table: HTMLTableElement | null = findAncestor(cornerButton, isTableElement, element);

        if (!table) {
          return;
        }

        if (isTableFullySelected(table)) {
          return;
        }

        tableEditionController.onCornerButtonDecoratorClicked(cornerButton);
      }),
    ),
    // Decorate a row / column when clicking on a handle.
    handleUnderClick$.pipe(
      tap(handle => {
        if (!handle) {
          return;
        }
        const table: HTMLTableElement | null = findAncestor(handle, isTableElement, element);
        if (!table) {
          return;
        }
        if (isHeaderDecoratorDecorated(handle)) {
          return;
        }
        if (isColumnHeaderDecorator(handle)) {
          tableEditionController.onColumnHeaderDecoratorClicked(handle);
        }
        if (isRowHeaderDecorator(handle)) {
          tableEditionController.onRowHeaderDecoratorClicked(handle);
        }
      }),
    ),
    // Decorate cells when they are selected.
    cellsSelectedPlan$.pipe(
      tap(selectionPlan => {
        tableEditionController.onSelectionPlanChange(selectionPlan);
      }),
    ),
    // Extend cells selection with shift + arrowKey shortcut.
    fromEvent<KeyboardEvent>(element, 'keydown').pipe(
      tap(event => {
        // Check if the right keys are pressed (shift + arrow key)
        if (!event.shiftKey) {
          return;
        }
        const { key } = event;
        if (!Object.values(arrowKeyCode).includes(key)) {
          return;
        }

        tableEditionController.onShiftArrowKeyPressed(key, event);
      }),
    ),
    // Navigate within table with up and down arrow keys.
    fromEvent<KeyboardEvent>(element, 'keydown').pipe(
      tap(event => {
        // Check if the right keys are pressed (up/down arrow key only)
        if (event.shiftKey || event.ctrlKey || event.metaKey) {
          return;
        }
        const { key } = event;
        if (![arrowKeyCode.UP, arrowKeyCode.DOWN].includes(key)) {
          return;
        }

        tableEditionController.onUpDownArrowKeyPressed(key, event);
      }),
    ),
    fromMouseTracking(element, document, window, event => {
      if (!isNode(event.target) || !isColumnResizeHandle(event.target)) {
        return undefined;
      }

      const cell = findAncestor(event.target, isTableCellElement, element);
      const table = findAncestor(event.target, isTableElement, element);
      if (!cell || !table) {
        return undefined;
      }

      return new ColumnResizeController(table, cell, event, undoService);
    }),
  ).pipe(share());
}

/**
 * Return a stream of tables that are mousedowned in element.
 * @param element
 */
function getTableUnderMousedownStream(element: Element) {
  let previousTable: HTMLTableElement | null = null;
  return merge(
    fromEvent<MouseEvent>(element, 'mousedown').pipe(
      map(event => {
        const { target } = event;
        if (!isNode(target)) {
          return null;
        }

        if (isTableElement(target)) {
          return target;
        }

        // Button panel get detached when they're clicked. We don't want to undecorate table in this case.
        const buttonPanelAncestor = findAncestor(target, isButtonPanel, element);
        if (buttonPanelAncestor) {
          return previousTable;
        }

        const tableAncestor = findAncestor(target, isTableElement, element);
        if (tableAncestor) {
          return tableAncestor;
        }

        const wrapper = findAncestor(target, isTableWrapper, element);
        if (wrapper) {
          return wrapper.querySelector('table');
        }

        return null;
      }),
    ),
    // Remove table decorator on blur.
    fromEvent(element, 'blur').pipe(mapTo(null)),
    fromEvent(element, 'contextmenu').pipe(mapTo(null)),
  ).pipe(
    tap(tableOrNull => {
      previousTable = tableOrNull;
      return tableOrNull;
    }),
    distinctUntilChanged(),
  );
}

/**
 * Return a stream of table handles that are clicked in element.
 * @param element
 */
function getHandleUnderClickStream(element: Element) {
  return merge(
    fromEvent<MouseEvent>(element, 'click').pipe(
      map(event => {
        const { target } = event;
        if (!isNode(target) || !isHeaderDecorator(target) || !target.parentElement) {
          return null;
        }

        return target;
      }),
    ),
    // Remove row / column decorator on blur.
    fromEvent(element, 'blur').pipe(mapTo(null)),
  );
}

/**
 * Return a stream of table handles that are clicked in element.
 * @param element
 */
function getTableCornerButtonUnderClickStream(element: Element) {
  return merge(
    fromEvent<MouseEvent>(element, 'click').pipe(
      map(event => {
        const { target } = event;
        if (!isNode(target) || !isTableCornerButtonDecorator(target) || !target.parentElement) {
          return null;
        }

        return target;
      }),
    ),
  );
}

/**
 * Returns a stream of tablePlan matching the current selection.
 */
function getSelectedCellsPlanStream(element: Element, selectionService: ElementSelectionService) {
  return merge(
    selectionService.changes$.pipe(
      map(() => {
        const { selection } = selectionService;
        if (!selection) {
          return null;
        }
        const { range } = selection;
        if (!range) {
          return null;
        }
        return getSelectedCellsPlan(element, range);
      }),
      distinctUntilChanged((prevSelectionPlan, selectionPlan) =>
        isSameTablePlan(prevSelectionPlan, selectionPlan),
      ),
    ),
  );
}

function getCanDecorateTable(table: HTMLTableElement, container: Element) {
  // Do not decorate table if it contains nested table.
  if (table.querySelectorAll('table').length > 0) {
    return false;
  }
  // Do not decorate table if it's a nested table.
  return !findAncestor(table, node => isTableElement(node) && node !== table, container);
}
